import React from 'react';
import { PacingBlockV2 } from 'shared/src/api/line-item-types';
import { LineItemType } from 'shared/src/domain/line-item/line-item-types';
import { ISODateRange } from 'shared/src/date-string-utils/types';
import { PacingChart } from '@/features/line-items/pacing-chart';
import { cumulativeSpend } from 'shared/src/domain/line-item/pacing-helpers';
import {
  blockUnits,
  dailyBudget,
  dailyUnits,
  genColor,
  unitShortLabel
} from '../utils/pacing-display';
import { LockedField } from '@/features/line-items/line-item-form/media-trader-form/media-team-line-item-form';
import { formatCurrency } from '@/components/tables/table-utils';
import { formatRange } from 'shared/src/date-string-utils/date-utils';
import { cn } from '@/lib/utils';
import { useChannelRatesContext } from '@/features/strategy/channel-rates-context';
import { calcIndividualBlockBudgetV2 } from 'shared/src/domain/line-item/line-item-utils';

type Props = {
  pacing_details: PacingBlockV2[] | null | undefined;
  dates: ISODateRange;
  unit_price_type_id: number;
  line_item_type: LineItemType;
  planned_unit_cost: number;
  planned_margin?: number | null;
  referral_commission_rate?: number | null;
  pacing_type?: 'lifetime' | 'custom';
  monthly_rollover?: boolean;
};

export function ReadOnlyCustomPacingField({
  pacing_details,
  dates,
  unit_price_type_id,
  line_item_type,
  planned_unit_cost,
  planned_margin,
  referral_commission_rate,
  pacing_type,
  monthly_rollover
}: Props) {
  const { unitPriceTypes } = useChannelRatesContext();
  const blocks = pacing_details || [];

  const isStandard = line_item_type === 'standard';

  // For standard line items show Media Budget per block (referral + margin
  // adjusted) to match the right-rail Pacing Schedule. Other types display
  // block.budget directly.
  const priceLabel = isStandard ? 'Media Budget' : 'Budget';
  const dailyPriceLabel = isStandard ? 'Daily Media $' : 'Daily Budget';

  if (blocks.length === 0) {
    return <div className="text-gray-500 text-sm">No custom pacing blocks defined</div>;
  }

  return (
    <>
      {monthly_rollover !== undefined && (
        <LockedField
          label="Month-to-Month"
          value={
            <div className="text-white text-sm">
              {monthly_rollover ? 'Rollover' : 'No rollover'}
            </div>
          }
        />
      )}
      <LockedField
        label="Pacing Schedule"
        value={
          <div className="flex min-w-0 flex-col">
            <div className="w-full min-w-0">
              <PacingChart mode="dark" data={cumulativeSpend(dates, blocks)} blocks={blocks} />
            </div>

            <div className="flex flex-col gap-2">
              {blocks.map((block, idx) => {
                const budget = isStandard
                  ? calcIndividualBlockBudgetV2(
                      block,
                      planned_margin,
                      referral_commission_rate ?? undefined,
                      undefined
                    )
                  : block.budget;
                const range = block.dates;
                return (
                  <div key={idx} className="flex gap-2">
                    <div className="flex items-center gap-2 text-sm text-white w-26">
                      <div className={cn(genColor(idx), 'w-2 h-full rounded-2xl')} />
                      <div>{pacing_type === 'lifetime' ? 'Lifetime' : `Block ${idx + 1}`}</div>
                    </div>
                    <div className="flex flex-col gap-2">
                      {formatRange(range)}
                      <div className="flex flex-1 w-full gap-4">
                        <ReadOnlyCalculatedField
                          label={priceLabel}
                          value={formatCurrency(budget)}
                        />
                        <ReadOnlyCalculatedField
                          value={dailyBudget(budget, range)}
                          label={dailyPriceLabel}
                        />
                        <ReadOnlyCalculatedField
                          value={blockUnits(budget, planned_unit_cost)}
                          label={unitShortLabel(unit_price_type_id, unitPriceTypes)}
                        />
                        <ReadOnlyCalculatedField
                          value={dailyUnits({ dates: range, budget }, planned_unit_cost)}
                          label={`Daily ${unitShortLabel(unit_price_type_id, unitPriceTypes)}`}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        }
      />
    </>
  );
}

function ReadOnlyCalculatedField({ value, label }: { value: string; label: string }) {
  if (!value) return null;
  return (
    <div className="flex rounded-md bg-gray-700 px-2 py-1 text-sm text-white">
      <div className="mr-2">{value}</div>
      <div className="font-light text-gray-400">{label}</div>
    </div>
  );
}
